import { Typography } from '@material-ui/core';
import type { ManagementTimelineMarker } from '@internal/spassu-code-contract';
import type { CockpitViewModel } from '../model/cockpitViewModel';
import { humanizeCode, relationKindLabel } from '../model/semanticLabels';
import { useCockpitStyles } from './cockpitStyles';

/**
 * Evidence and lineage of a timeline marker, revealed inside its own card. The
 * card header already carries title, summary, type, outcome and timestamp.
 */
export const TimelineMarkerDetail = ({
  marker,
  model,
}: {
  marker: ManagementTimelineMarker;
  model: CockpitViewModel;
}) => {
  const classes = useCockpitStyles();
  const relations = model.relations.filter(relation =>
    relationTouchesMarker(relation, marker),
  );
  return (
    <div className={classes.timelineMarkerReveal}>
      <Typography variant="body2">
        Origem: {humanizeCode(marker.source)} · proveniência:{' '}
        {humanizeCode(marker.provenance)} · fidelidade:{' '}
        {humanizeCode(marker.fidelity)}
      </Typography>
      <MarkerEvidence evidenceIds={marker.evidenceIds ?? []} />
      <MarkerRelations relations={relations} />
    </div>
  );
};

function MarkerEvidence({ evidenceIds }: { evidenceIds: string[] }) {
  if (evidenceIds.length === 0) {
    return <Typography variant="body2">Sem evidência vinculada.</Typography>;
  }
  return (
    <section aria-label="Evidências do marco">
      <Typography variant="subtitle2">Evidências</Typography>
      <ul>
        {evidenceIds.map(id => (
          <li key={id}>{id}</li>
        ))}
      </ul>
    </section>
  );
}

function MarkerRelations({
  relations,
}: {
  relations: CockpitViewModel['relations'];
}) {
  if (relations.length === 0) return null;
  return (
    <section aria-label="Relações do marco">
      <Typography variant="subtitle2">Relações</Typography>
      <ul>
        {relations.map(relation => (
          <li key={relation.id}>
            {relationKindLabel(relation.kind)}: {relation.from.id} →{' '}
            {relation.to.id}
          </li>
        ))}
      </ul>
    </section>
  );
}

function relationTouchesMarker(
  relation: CockpitViewModel['relations'][number],
  marker: ManagementTimelineMarker,
): boolean {
  const ids = new Set([marker.id, ...(marker.evidenceIds ?? [])]);
  return (
    ids.has(relation.from.id) ||
    ids.has(relation.to.id) ||
    relation.evidenceIds.some(id => ids.has(id))
  );
}
